// deployment_class: customer_env_allowed
/**
 * Auth store — Zustand. Holds the authenticated session + organizations +
 * scopes after /v3/auth/me resolves. Plan 14 §Task 3.
 *
 * Session storage: per OQ-Sh1 (locked), the session lives in an httpOnly
 * cookie set by Better Auth (`/api/auth/*`). The store does NOT hold the
 * token; it holds the user/scopes/orgs that `/v3/auth/me` returns.
 */
import { create } from 'zustand';

export interface AuthUser {
  id: string;
  email: string;
  full_name: string | null;
  /**
   * The canonical rbac Role (Role-View Wave 1b) surfaced by `/v3/auth/me`. Drives
   * the Layer-B landing map (`homeRouteFor`) + the effective-role seam. Kept as a
   * string (not the backend `Role` union) so the FE store has no backend-type
   * dependency; `landing-map.ts` narrows it.
   */
  role: string;
  /**
   * Optional profile picture URL (Settings & Account IA §16 · bug_UI_1b). When
   * present, the account chip renders it; otherwise it falls back to initials.
   * Upload/persistence lands with the §16 account-ops write surface (Wave 2);
   * this is the display seam so the chip is picture-ready today.
   */
  avatar_url?: string | null;
}

export interface AuthOrgMembership {
  id: string;
  name: string;
  role: string;
}

export interface AuthSnapshot {
  user: AuthUser | null;
  scopes: string[];
  organizations: AuthOrgMembership[];
  activeOrgId: string | null;
  /**
   * 'idle' before the first /v3/auth/me; 'loading' during the call;
   * 'authenticated' on success; 'unauthenticated' on 401; 'error' on other
   * failures.
   */
  status: 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';
  error: string | null;
}

export interface AuthStore extends AuthSnapshot {
  setLoading: () => void;
  setAuthenticated: (snap: Omit<AuthSnapshot, 'status' | 'error'>) => void;
  setUnauthenticated: () => void;
  setError: (msg: string) => void;
  setActiveOrg: (orgId: string | null) => void;
  reset: () => void;
}

const INITIAL: AuthSnapshot = {
  user: null,
  scopes: [],
  organizations: [],
  activeOrgId: null,
  status: 'idle',
  error: null,
};

export const useAuthStore = create<AuthStore>((set) => ({
  ...INITIAL,
  setLoading: () => set({ status: 'loading', error: null }),
  setAuthenticated: (snap) =>
    set({
      ...snap,
      status: 'authenticated',
      error: null,
    }),
  setUnauthenticated: () =>
    set({
      ...INITIAL,
      status: 'unauthenticated',
    }),
  setError: (msg: string) => set({ status: 'error', error: msg }),
  setActiveOrg: (orgId) => set({ activeOrgId: orgId }),
  reset: () => set({ ...INITIAL }),
}));
